/** @jsx jsx */

import { jsx, css } from '@emotion/core';

import { Button, Heading, Text, Spacer } from '@zapier/design-system';
import { FlexLayout, Modal } from '@zapier/common-components';

import { Confetti } from '@zapier/design-system-revenue/src/components/private';
import { ChangeType, PlanV2 } from '@zapier/design-system-revenue/src/types';
import { isFree, useSize } from '@zapier/design-system-revenue/src/utils';

const containerStyles = css`
  text-align: center;
`;

const getHeadingText = (changeType: ChangeType) => {
  switch (changeType) {
    case 'upgrade':
      return 'Thanks for upgrading!';
    case 'downgrade':
      return 'Downgrade complete';
    case 'change':
      return 'Change complete';
    default:
      return '';
  }
};

const buildContent = ({
  changeType,
  newPlan,
}: {
  changeType: ChangeType;
  newPlan: PlanV2;
}) => {
  let planStatement = '';
  let payingStatement = '';

  if (newPlan.allowCompanies || newPlan.hasTeams) {
    planStatement = `You just created a ${newPlan.type} -- well done!`;
  } else {
    planStatement = `You're now on the ${newPlan.name} plan`;
    payingStatement = isFree(newPlan) ? '' : `paying ${newPlan.interval}ly`;
  }
  const punctuation = changeType === 'upgrade' ? '!' : '.';
  return `${planStatement} ${payingStatement}${punctuation}`.replace(
    ` ${punctuation}`,
    punctuation
  );
};

const getPrimaryButtonProps = ({
  hasPausedZaps,
  newPlan,
}: {
  hasPausedZaps: boolean;
  newPlan: PlanV2;
}) => {
  if (newPlan.allowCompanies) {
    return {
      href: '/app/settings/organization',
      text: 'Start Setup',
    };
  }
  if (newPlan.hasTeams) {
    return {
      href: '/app/settings/team',
      text: 'Invite Team Members',
    };
  }
  if (hasPausedZaps) {
    return {
      href: '/app/zaps/folder/paused',
      text: 'View Paused Zaps',
    };
  }
  return {
    href: '/app/zaps',
    text: 'Go to My Zaps',
  };
};

const getSecondaryButtonProps = ({
  hasPausedZaps,
  newPlan,
}: {
  hasPausedZaps: boolean;
  newPlan: PlanV2;
}) => {
  if (newPlan.allowCompanies) {
    return {
      href: '/app/dashboard',
      text: 'Go to Dashboard',
    };
  }
  if (newPlan.hasTeams) {
    return {
      href: '/app/zaps',
      text: 'Go to Team Zaps',
    };
  }
  if (!hasPausedZaps) {
    return {
      href: '/app/billing/plans',
      text: '← Plans and Pricing',
    };
  }
  return null;
};

type Props = {
  /** The ChangeType for this plan change (`upgrade`, `downgrade` or `change`) */
  changeType: ChangeType;
  /** Indicates whether the plan change paused a user's zaps */
  hasPausedZaps?: boolean;
  /** The new plan the user has changed to */
  newPlan: PlanV2;
};

export const ChangePlanCompleteModal = ({
  changeType,
  hasPausedZaps = false,
  newPlan,
}: Props) => {
  const size = useSize();

  const heading = getHeadingText(changeType);
  const content = buildContent({
    changeType,
    newPlan,
  });

  const secondaryButtonProps = getSecondaryButtonProps({
    hasPausedZaps,
    newPlan,
  });
  const primaryButtonProps = getPrimaryButtonProps({
    hasPausedZaps,
    newPlan,
  });

  return (
    <Modal
      ariaDescribedBy="modal-description"
      ariaLabelledBy="modal-title"
      closeOnOutsideClick={false}
      overflowY="hidden" // ensures confetti doesn't make the modal scrollable
    >
      <div css={containerStyles} id="change-plan-complete-modal">
        {changeType === 'upgrade' && <Confetti />}
        <Heading>
          <Text type="header800">
            <span id="modal-title">{heading}</span>
          </Text>
        </Heading>

        <Spacer height={20} />
        <Text type="bodyText900">
          <span id="modal-description">{content}</span>
        </Text>
        <Spacer height={30} />

        <FlexLayout
          direction={size === 'small' ? 'column' : 'row'}
          gutter="normal"
          justify="center"
        >
          {secondaryButtonProps && (
            <Button
              color="greyAlt"
              href={secondaryButtonProps.href}
              isFullWidth={size === 'small'}
            >
              {secondaryButtonProps.text}
            </Button>
          )}
          <Button href={primaryButtonProps.href} isFullWidth={size === 'small'}>
            {primaryButtonProps.text}
          </Button>
        </FlexLayout>
      </div>
    </Modal>
  );
};
